import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { getUnitSymbols } from '../../../utils/proj4/units';
import { FlySpeed } from '../../../viewer/cameras/fly-speed';
import { viewerZIndices } from '../../../viewer/viewer-z-indices';

/** How long the speed shows after it changes, in ms. */
const visibleTime = 1500;

/** Shows the free flight's speed in the middle of the view for a moment, each time it changes. */
const FlySpeedIndicator = () => {
  const { t } = useTranslation();
  const [shown, setShown] = React.useState<{ speed: number; unit: string }>();
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = FlySpeed.instance.subscribe(() => {
      setShown({
        speed: Math.round(FlySpeed.instance.speed * 10) / 10,
        unit: getUnitSymbols(FlySpeed.instance.unit).length,
      });
      setVisible(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setVisible(false), visibleTime);
    });
    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  if (!shown) {
    return null;
  }

  return (
    <Component $visible={visible} role="status">
      {t('viewSettings.cameraControl.fly.speed', {
        ns: 'skyviewToolbar',
        speed: shown.speed,
        unit: shown.unit,
      })}
    </Component>
  );
};

// Dark and slightly translucent, as the panel with the cameras, fading out
const Component = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${viewerZIndices.toolbar};
  pointer-events: none;

  padding: 0.5em 1em;
  border-radius: 0.5em;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background-color: color-mix(
    in srgb,
    ${(props) => props.theme.color.gray.darkest} 80%,
    transparent
  );

  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity ${(props) => (props.$visible ? '0s' : '0.5s')};
`;

export { FlySpeedIndicator };
